import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

// ── helpers ──────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const load = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
};
const save = (key, val) => localStorage.setItem(key, JSON.stringify(val));

// Seed some demo data on first run
const seedData = () => {
  if (load('cr_seeded', false)) return;

  const demoUsers = [
    {
      id: 'demo1', email: 'alex.johnson@mit.edu', password: 'Demo1234!',
      name: 'Alex Johnson', university: 'MIT', hometown: 'Boston, MA',
      bio: 'Computer science junior. Love hiking and road trips!',
      photo: '', isDriver: true, isRider: true,
      car: { make: 'Honda', model: 'Civic', color: 'Blue' },
      completedRides: 14, rating: 4.8, ratingCount: 12,
      memberSince: '2024-09-01', isVerified: true,
    },
    {
      id: 'demo2', email: 'sarah.m@stanford.edu', password: 'Demo1234!',
      name: 'Sarah Martinez', university: 'Stanford', hometown: 'San Jose, CA',
      bio: 'Pre-med student. Coffee addict ☕',
      photo: '', isDriver: false, isRider: true,
      car: null,
      completedRides: 8, rating: 4.9, ratingCount: 7,
      memberSince: '2024-08-15', isVerified: true,
    },
    {
      id: 'demo3', email: 'ryan.k@ucla.edu', password: 'Demo1234!',
      name: 'Ryan Kim', university: 'UCLA', hometown: 'Los Angeles, CA',
      bio: 'Film student. Always down for a good conversation.',
      photo: '', isDriver: true, isRider: true,
      car: { make: 'Toyota', model: 'Prius', color: 'Silver' },
      completedRides: 22, rating: 4.7, ratingCount: 19,
      memberSince: '2024-01-10', isVerified: true,
    },
  ];

  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const d1 = new Date(today); d1.setDate(today.getDate() + 2);
  const d2 = new Date(today); d2.setDate(today.getDate() + 3);
  const d3 = new Date(today); d3.setDate(today.getDate() + 5);

  const demoRides = [
    {
      id: 'ride1', driverId: 'demo1',
      date: fmt(d1), time: '08:00',
      departure: 'MIT Campus, Cambridge MA',
      destination: 'South Station, Boston MA',
      seats: 3, seatsAvailable: 2, price: 8,
      status: 'open', requests: [],
      createdAt: new Date().toISOString(),
    },
    {
      id: 'ride2', driverId: 'demo3',
      date: fmt(d2), time: '14:30',
      departure: 'UCLA Westwood',
      destination: 'LAX Airport',
      seats: 3, seatsAvailable: 1, price: 15,
      status: 'open', requests: [],
      createdAt: new Date().toISOString(),
    },
    {
      id: 'ride3', driverId: 'demo1',
      date: fmt(d3), time: '17:00',
      departure: 'MIT Campus, Cambridge MA',
      destination: 'Providence, RI',
      seats: 2, seatsAvailable: 2, price: 20,
      status: 'open', requests: [],
      createdAt: new Date().toISOString(),
    },
  ];

  save('cr_users', demoUsers);
  save('cr_rides', demoRides);
  save('cr_conversations', []);
  save('cr_seeded', true);
};

// ── Provider ─────────────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => load('cr_session', null));
  const [users, setUsers] = useState(() => { seedData(); return load('cr_users', []); });
  const [rides, setRides] = useState(() => load('cr_rides', []));
  const [conversations, setConversations] = useState(() => load('cr_conversations', []));

  // Persist changes
  useEffect(() => { save('cr_users', users); }, [users]);
  useEffect(() => { save('cr_rides', rides); }, [rides]);
  useEffect(() => { save('cr_conversations', conversations); }, [conversations]);
  useEffect(() => { save('cr_session', currentUser); }, [currentUser]);

  // ── Auth ────────────────────────────────────────────────────────────────────
  const register = useCallback((data) => {
    const { email, password, name, university, hometown, bio, isDriver, car } = data;
    if (!email.toLowerCase().endsWith('.edu')) {
      return { error: 'Please use a valid .edu email address.' };
    }
    const existing = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (existing) return { error: 'An account with this email already exists.' };

    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
    const newUser = {
      id: uid(), email, password, name, university, hometown, bio: bio || '',
      photo: '',
      isDriver: isDriver || false, isRider: true,
      car: isDriver ? car : null,
      completedRides: 0, rating: 0, ratingCount: 0,
      memberSince: new Date().toISOString().split('T')[0],
      isVerified: false, verificationCode,
    };
    setUsers(prev => [...prev, newUser]);
    return { success: true, userId: newUser.id, verificationCode };
  }, [users]);

  const verifyEmail = useCallback((userId, code) => {
    let found = false;
    setUsers(prev => prev.map(u => {
      if (u.id === userId && u.verificationCode === code) {
        found = true;
        return { ...u, isVerified: true, verificationCode: null };
      }
      return u;
    }));
    if (!found) {
      // re-check after state update
      const u = users.find(u2 => u2.id === userId);
      if (u && u.verificationCode === code) {
        found = true;
        setUsers(prev => prev.map(u2 =>
          u2.id === userId ? { ...u2, isVerified: true, verificationCode: null } : u2
        ));
      }
    }
    const user = users.find(u => u.id === userId);
    if (user && user.verificationCode === code) return { success: true };
    return { error: 'Invalid verification code.' };
  }, [users]);

  const login = useCallback((email, password) => {
    const user = users.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );
    if (!user) return { error: 'Invalid email or password.' };
    if (!user.isVerified) return { error: 'Please verify your email first.', userId: user.id };
    setCurrentUser(user);
    return { success: true };
  }, [users]);

  const logout = useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem('cr_session');
  }, []);

  const updateUser = useCallback((userId, updates) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
    setCurrentUser(prev => prev?.id === userId ? { ...prev, ...updates } : prev);
  }, []);

  const refreshCurrentUser = useCallback(() => {
    if (!currentUser) return;
    const fresh = users.find(u => u.id === currentUser.id);
    if (fresh) setCurrentUser(fresh);
  }, [currentUser, users]);

  // ── Rides ───────────────────────────────────────────────────────────────────
  const postRide = useCallback((data) => {
    const ride = {
      id: uid(),
      driverId: currentUser.id,
      ...data,
      seatsAvailable: data.seats,
      status: 'open',
      requests: [],
      createdAt: new Date().toISOString(),
    };
    setRides(prev => [ride, ...prev]);
    return { success: true, rideId: ride.id };
  }, [currentUser]);

  const requestRide = useCallback((rideId) => {
    let err = null;
    setRides(prev => prev.map(r => {
      if (r.id !== rideId) return r;
      if (r.driverId === currentUser.id) { err = 'You cannot request your own ride.'; return r; }
      const already = r.requests.find(req => req.riderId === currentUser.id);
      if (already) { err = 'You already requested this ride.'; return r; }
      return { ...r, requests: [...r.requests, { riderId: currentUser.id, status: 'pending' }] };
    }));
    if (err) return { error: err };
    return { success: true };
  }, [currentUser]);

  const respondToRequest = useCallback((rideId, riderId, action) => {
    setRides(prev => prev.map(r => {
      if (r.id !== rideId) return r;
      const updatedRequests = r.requests.map(req =>
        req.riderId === riderId ? { ...req, status: action } : req
      );
      const acceptedCount = updatedRequests.filter(req => req.status === 'accepted').length;
      const seatsAvailable = r.seats - acceptedCount;
      return {
        ...r,
        requests: updatedRequests,
        seatsAvailable,
        status: seatsAvailable === 0 ? 'full' : 'open',
      };
    }));
    if (action === 'accepted') {
      // Create or find conversation
      const existingConv = conversations.find(
        c => c.rideId === rideId && c.participants.includes(riderId) && c.participants.includes(currentUser.id)
      );
      if (!existingConv) {
        const conv = {
          id: uid(), rideId,
          participants: [currentUser.id, riderId],
          messages: [{
            id: uid(), senderId: 'system',
            text: 'You\'ve been matched! Feel free to coordinate your ride details here.',
            timestamp: new Date().toISOString(),
          }],
          updatedAt: new Date().toISOString(),
        };
        setConversations(prev => [conv, ...prev]);
      }
    }
    return { success: true };
  }, [conversations, currentUser]);

  const cancelRide = useCallback((rideId) => {
    setRides(prev => prev.map(r =>
      r.id === rideId ? { ...r, status: 'cancelled' } : r
    ));
  }, []);

  // ── Messages ─────────────────────────────────────────────────────────────────
  const sendMessage = useCallback((convId, text) => {
    const msg = {
      id: uid(), senderId: currentUser.id,
      text, timestamp: new Date().toISOString(),
    };
    setConversations(prev => prev.map(c =>
      c.id === convId
        ? { ...c, messages: [...c.messages, msg], updatedAt: msg.timestamp }
        : c
    ));
  }, [currentUser]);

  const getUserById = useCallback((id) => users.find(u => u.id === id), [users]);

  const getRideById = useCallback((id) => rides.find(r => r.id === id), [rides]);

  const getMyConversations = useCallback(() => {
    if (!currentUser) return [];
    return conversations
      .filter(c => c.participants.includes(currentUser.id))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [conversations, currentUser]);

  const value = {
    currentUser, users, rides, conversations,
    register, verifyEmail, login, logout, updateUser, refreshCurrentUser,
    postRide, requestRide, respondToRequest, cancelRide,
    sendMessage, getUserById, getRideById, getMyConversations,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
